<script setup>
import { ref, computed, nextTick, watch } from 'vue'
import { Card } from 'primevue'
import InputNumber from 'primevue/inputnumber'
import ANButton from '@/components/common/AnButton.vue'
import Chart from 'primevue/chart'
import { calculateRetirementFundingGap } from '@/utils/financeCalculators'

// Form data
const formData = ref({
  dateOfBirth: '1987-01-01',
  retirementAgeYears: 60,
  lifeExpectancyYears: 80,
  currentMonthlyExpense: 50000,
  currentSavings: 500000,
  retirementBenefitsLumpSum: 1000000,
  inflation: 6,
  expectedReturnWorking: 12,
  expectedReturnRetired: 8,
  expenseReductionPct: 20
})

const result = ref(null)
const chartData = ref({})
const chartOptions = ref({})

// Computed properties
const isValidInput = computed(() => {
  return formData.value.dateOfBirth &&
         formData.value.retirementAgeYears > 0 &&
         formData.value.lifeExpectancyYears > 0 &&
         formData.value.currentMonthlyExpense > 0 &&
         formData.value.inflation > 0 &&
         formData.value.expectedReturnWorking > 0 &&
         formData.value.expectedReturnRetired > 0
})

// Calculate retirement funding gap
const calculate = () => {
  if (!isValidInput.value) return

  try {
    result.value = calculateRetirementFundingGap(formData.value)
    if (!result.value.error) {
      generateChart()
      // Scroll to results section after calculation
      setTimeout(() => {
        const resultsElement = document.querySelector('.result-card')
        if (resultsElement) {
          resultsElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          })
        }
      }, 100)
    }
  } catch (error) {
    result.value = { error: 'Calculation error: ' + error.message }
  }
}

// Generate chart data
const generateChart = () => {
  if (!result.value || result.value.error) return

  const data = result.value
  chartData.value = {
    labels: ['Required Corpus', 'Current Savings (FV)', 'Retirement Benefits', 'Funding Gap'],
    datasets: [{
      label: 'Retirement Planning (₹)',
      data: [
        data.pvOfAnnuityNeededAtRetirement,
        data.fvSavingsAtRetirement,
        formData.value.retirementBenefitsLumpSum,
        Math.max(0, data.fundingGapAtRetirement)
      ],
      backgroundColor: [
        'rgba(255, 99, 132, 0.6)',   // Required - Red
        'rgba(54, 162, 235, 0.6)',   // Current Savings - Blue
        'rgba(75, 192, 192, 0.6)',   // Benefits - Teal
        'rgba(255, 206, 86, 0.6)'    // Gap - Yellow
      ],
      borderColor: [
        'rgba(255, 99, 132, 1)',
        'rgba(54, 162, 235, 1)',
        'rgba(75, 192, 192, 1)',
        'rgba(255, 206, 86, 1)'
      ],
      borderWidth: 1
    }]
  }

  chartOptions.value = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Retirement Corpus Breakdown'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.parsed
            return context.label + ': ₹' + formatCurrency(value)
          }
        }
      }
    }
  }
}

// Format currency
const formatCurrency = (amount) => {
  if (amount >= 10000000) { // 1 crore
    return (amount / 10000000).toFixed(1) + 'Cr'
  } else if (amount >= 100000) { // 1 lakh
    return (amount / 100000).toFixed(1) + 'L'
  } else {
    return amount.toLocaleString('en-IN')
  }
}

// Format date
const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

// Initialize
calculate()
</script>

<template>
  <div class="retirement-corpus-calculator">
    <div class="calculator-grid">
      <!-- Input Form -->
      <Card class="input-card">
        <template #title>
          <h3>Retirement Corpus Calculator Input</h3>
        </template>
        <template #content>
          <div class="form-fields">
            <!-- Personal Details Section -->
            <div class="section-title">Personal Details</div>

            <!-- Date of Birth -->
            <div class="field">
              <label for="dateOfBirth">Date of Birth</label>
              <input
                id="dateOfBirth"
                v-model="formData.dateOfBirth"
                type="date"
                class="input-field date-input"
              />
            </div>

            <!-- Retirement Age -->
            <div class="field">
              <label for="retirementAge">Retirement Age (Years)</label>
              <InputNumber
                id="retirementAge"
                v-model="formData.retirementAgeYears"
                :min="35"
                :max="65"
                :step="1"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <!-- Life Expectancy -->
            <div class="field">
              <label for="lifeExpectancy">Life Expectancy (Years)</label>
              <InputNumber
                id="lifeExpectancy"
                v-model="formData.lifeExpectancyYears"
                :min="65"
                :max="95"
                :step="1"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <!-- Financial Details Section -->
            <div class="section-title">Financial Information</div>

            <!-- Current Monthly Expenses -->
            <div class="field">
              <label for="currentExpense">Current Monthly Expenses (₹)</label>
              <InputNumber
                id="currentExpense"
                v-model="formData.currentMonthlyExpense"
                :min="5000"
                :max="1000000"
                :step="5000"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <!-- Current Savings -->
            <div class="field">
              <label for="currentSavings">Current Savings/Investments (₹)</label>
              <InputNumber
                id="currentSavings"
                v-model="formData.currentSavings"
                :min="0"
                :max="100000000"
                :step="50000"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <!-- Retirement Benefits -->
            <div class="field">
              <label for="retirementBenefits">Expected Retirement Benefits (₹)</label>
              <InputNumber
                id="retirementBenefits"
                v-model="formData.retirementBenefitsLumpSum"
                :min="0"
                :max="50000000"
                :step="100000"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
              <small class="help-text">PF, Pension, Gratuity, etc.</small>
            </div>

            <!-- Assumptions Section -->
            <div class="section-title">Assumptions</div>

            <!-- Inflation Rate -->
            <div class="field">
              <label for="inflation">Inflation Rate (%)</label>
              <InputNumber
                id="inflation"
                v-model="formData.inflation"
                :min="1"
                :max="15"
                :step="0.5"
                :minFractionDigits="1"
                :maxFractionDigits="2"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <!-- Expected Return - Working Years -->
            <div class="field">
              <label for="returnWorking">Expected Return - Working Years (%)</label>
              <InputNumber
                id="returnWorking"
                v-model="formData.expectedReturnWorking"
                :min="5"
                :max="25"
                :step="0.5"
                :minFractionDigits="1"
                :maxFractionDigits="2"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
            </div>

            <!-- Expected Return - Retirement Years -->
            <div class="field">
              <label for="returnRetired">Expected Return - Retirement Years (%)</label>
              <InputNumber
                id="returnRetired"
                v-model="formData.expectedReturnRetired"
                :min="4"
                :max="15"
                :step="0.5"
                :minFractionDigits="1"
                :maxFractionDigits="2"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
              <small class="help-text">Conservative return during retirement</small>
            </div>

            <!-- Expense Reduction -->
            <div class="field">
              <label for="expenseReduction">Expense Reduction in Retirement (%)</label>
              <InputNumber
                id="expenseReduction"
                v-model="formData.expenseReductionPct"
                :min="0"
                :max="50"
                :step="5"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
              <small class="help-text">Expected reduction in expenses post-retirement</small>
            </div>

            <!-- Calculate Button -->
            <ANButton
              label="Calculate"
              severity="primary"
              size="large"
              @click="calculate"
              :disabled="!isValidInput"
              class="calculate-btn"
            />
          </div>
        </template>
      </Card>

      <!-- Results -->
      <Card v-if="result && !result.error" class="result-card">
        <template #title>
          <h3>Retirement Planning Results</h3>
        </template>
        <template #content>
          <div class="results">
            <!-- Main Result -->
            <div class="result-item highlight">
              <div class="result-label">
                {{ result.hasSurplus ? 'Surplus Fund' : 'Funding Gap' }}
              </div>
              <div class="result-value" :class="{ surplus: result.hasSurplus, gap: !result.hasSurplus }">
                ₹{{ formatCurrency(Math.abs(result.fundingGapAtRetirement)) }}
              </div>
              <div class="result-subtitle">
                @ the time of retirement ({{ formatDate(result.retirementDate) }})
              </div>
            </div>

            <!-- Summary Information -->
            <div class="result-summary">
              <div class="summary-item">
                <span class="summary-label">Current Age:</span>
                <span class="summary-value">{{ result.currentAgeYears }} years</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Years to Retirement:</span>
                <span class="summary-value">{{ result.yearsToRetirement }} years</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Years in Retirement:</span>
                <span class="summary-value">{{ result.yearsInRetirement }} years</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Required Corpus:</span>
                <span class="summary-value">₹{{ formatCurrency(result.pvOfAnnuityNeededAtRetirement) }}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Available Funds:</span>
                <span class="summary-value">₹{{ formatCurrency(result.totalFundsAtRetirement) }}</span>
              </div>
            </div>

            <!-- Monthly Expenses Breakdown -->
            <div class="expense-breakdown">
              <h4>Monthly Expenses</h4>
              <div class="breakdown-grid">
                <div class="breakdown-item">
                  <div class="breakdown-label">Current</div>
                  <div class="breakdown-value">₹{{ formData.currentMonthlyExpense.toLocaleString('en-IN') }}</div>
                </div>
                <div class="breakdown-item">
                  <div class="breakdown-label">At Retirement</div>
                  <div class="breakdown-value">₹{{ formatCurrency(result.monthlyExpensesAfterReduction) }}</div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </Card>
    </div>

    <!-- Chart -->
    <Card v-if="result && !result.error && chartData.labels" class="chart-card">
      <template #title>
        <h3>Retirement Corpus Breakdown</h3>
      </template>
      <template #content>
        <div class="chart-container">
          <Chart type="doughnut" :data="chartData" :options="chartOptions" class="chart" />
        </div>
      </template>
    </Card>

    <!-- Planning Tips -->
    <Card class="tips-card">
      <template #title>
        <h3>💡 Retirement Planning Tips</h3>
      </template>
      <template #content>
        <div class="tips-grid">
          <div class="tip-item">
            <div class="tip-icon">⏰</div>
            <div class="tip-content">
              <h4>Start Early</h4>
              <p>The earlier you start, the more time your money has to compound and grow.</p>
            </div>
          </div>
          <div class="tip-item">
            <div class="tip-icon">📊</div>
            <div class="tip-content">
              <h4>Review Regularly</h4>
              <p>Review your retirement plan annually and adjust based on life changes.</p>
            </div>
          </div>
          <div class="tip-item">
            <div class="tip-icon">💰</div>
            <div class="tip-content">
              <h4>Increase Savings Rate</h4>
              <p>Gradually increase your savings rate with salary increments and bonuses.</p>
            </div>
          </div>
          <div class="tip-item">
            <div class="tip-icon">🎯</div>
            <div class="tip-content">
              <h4>Conservative Approach</h4>
              <p>Use conservative return estimates for retirement years to be safe.</p>
            </div>
          </div>
        </div>
      </template>
    </Card>

    <!-- Error Display -->
    <Card v-if="result && result.error" class="error-card">
      <template #content>
        <div class="error-message">
          <i class="pi pi-exclamation-triangle"></i>
          <span>{{ result.error }}</span>
        </div>
      </template>
    </Card>
  </div>
</template>

<style scoped>
.retirement-corpus-calculator {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.calculator-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.section-title {
  font-weight: 700;
  color: var(--an-primary);
  font-size: 16px;
  margin-top: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--an-primary);
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field label {
  font-weight: 600;
  color: var(--an-text-primary);
  font-size: 14px;
}

.help-text {
  color: var(--an-text-secondary);
  font-size: 12px;
  font-style: italic;
}

.input-field {
  width: 100%;
  padding: 10px 12px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
}

.input-field:focus {
  outline: none;
  border-color: var(--an-primary);
}

.date-input {
  font-family: inherit;
}

.calculate-btn {
  width: 100%;
  margin-top: 16px;
}

.results {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.result-item {
  text-align: center;
  padding: 20px;
  border-radius: 8px;
  background: var(--an-background-light);
}

.result-item.highlight {
  background: linear-gradient(135deg, var(--an-primary), var(--an-secondary));
  color: var(--an-white);
}

.result-label {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  opacity: 0.9;
}

.result-value {
  font-size: 2rem;
  font-weight: 700;
}

.result-value.surplus {
  color: var(--an-success);
}

.result-value.gap {
  color: var(--an-error);
}

.result-item.highlight .result-value {
  color: inherit;
}

.result-subtitle {
  font-size: 0.9rem;
  color: var(--an-text-secondary);
  font-weight: 500;
  margin-top: 8px;
}

.result-item.highlight .result-subtitle {
  color: inherit;
  opacity: 0.9;
}

.result-summary {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid var(--an-gray3);
}

.summary-item:last-child {
  border-bottom: none;
}

.summary-label {
  color: var(--an-text-secondary);
  font-weight: 500;
}

.summary-value {
  font-weight: 600;
  color: var(--an-text-primary);
}

.expense-breakdown h4 {
  margin: 0 0 16px 0;
  color: var(--an-text-primary);
  font-size: 16px;
}

.breakdown-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.breakdown-item {
  text-align: center;
  padding: 16px;
  background: var(--an-background-light);
  border-radius: 8px;
  border: 1px solid var(--an-gray3);
}

.breakdown-label {
  font-size: 12px;
  color: var(--an-text-secondary);
  margin-bottom: 4px;
  font-weight: 500;
}

.breakdown-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--an-text-primary);
}

.chart-container {
  height: 400px;
  width: 100%;
}

.chart {
  height: 100% !important;
  width: 100% !important;
}

.tips-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.tip-item {
  display: flex;
  gap: 16px;
  padding: 20px;
  border-radius: 8px;
  background: var(--an-background-light);
  border-left: 4px solid var(--an-primary);
}

.tip-icon {
  font-size: 2rem;
  flex-shrink: 0;
}

.tip-content h4 {
  margin: 0 0 8px 0;
  color: var(--an-text-primary);
  font-size: 16px;
}

.tip-content p {
  margin: 0;
  color: var(--an-text-secondary);
  line-height: 1.5;
  font-size: 14px;
}

.error-card {
  background: var(--an-error-light);
  border: 1px solid var(--an-error);
}

.error-message {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--an-error);
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 768px) {
  .calculator-grid {
    grid-template-columns: 1fr;
  }

  .breakdown-grid {
    grid-template-columns: 1fr;
  }

  .result-value {
    font-size: 1.5rem;
  }

  .chart-container {
    height: 300px;
  }

  .tips-grid {
    grid-template-columns: 1fr;
  }
}
</style>
